import {
  AlertTriangle, ChevronLeft, ChevronRight,
  MoreHorizontal, X,
} from "lucide-react";
import { CSSProperties, ReactNode, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import "../i18n";
import { api, type Task, type User } from "../api";
import { difficultyRank, difficultyText, initials, statusText } from "../lib/format";
import { kanbanStyleClass } from "../lib/preferences";

export function PageHead({ title, meta, back, controls }: { title: string; meta?: ReactNode; back?: ReactNode; controls?: ReactNode }) {
  return <div className="page-head">
    <div>{back}<h1>{title}</h1>{meta && <div className="page-head-meta">{meta}</div>}</div>
    {controls && <div className="page-head-controls">{controls}</div>}
  </div>;
}

export { Loading } from "./Loading";

/** Portal keeps row actions visible outside scrollable tables. */
export function RowActionMenu({ label, actions, disabled = false }: {
  label: string; disabled?: boolean;
  actions: { label: string; onClick: () => void; danger?: boolean; disabled?: boolean; title?: string }[];
}) {
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; right: number } | null>(null);
  useEffect(() => {
    if (!position) return;
    menu.current?.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus();
    const dismiss = (event: Event) => {
      if (!menu.current?.contains(event.target as Node) && !trigger.current?.contains(event.target as Node)) setPosition(null);
    };
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') { event.preventDefault(); setPosition(null); trigger.current?.focus(); } };
    const close = () => setPosition(null);
    document.addEventListener('pointerdown', dismiss);
    document.addEventListener('keydown', escape);
    window.addEventListener('resize', close);
    return () => { document.removeEventListener('pointerdown', dismiss); document.removeEventListener('keydown', escape); window.removeEventListener('resize', close); };
  }, [position]);
  return <>
    <button ref={trigger} type="button" className="icon-button row-menu-trigger" aria-label={label} aria-expanded={!!position}
      disabled={disabled || !actions.length} title={disabled || !actions.length ? '변경 권한이 없습니다' : label}
      onClick={() => { const rect = trigger.current!.getBoundingClientRect(); setPosition(position ? null : { top: Math.max(8, Math.min(rect.bottom + 5, window.innerHeight - actions.length * 38 - 16)), right: Math.max(8, window.innerWidth - rect.right) }); }}><MoreHorizontal size={16} /></button>
    {position && createPortal(<div ref={menu} className="row-action-menu" role="group" aria-label={`${label} 메뉴`} style={position}
      onKeyDown={event => { if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); setPosition(null); trigger.current?.focus(); } }}>
      {actions.map(action => <button key={action.label} type="button" className={action.danger ? 'danger' : ''} disabled={action.disabled} title={action.title}
        onClick={() => { setPosition(null); trigger.current?.focus(); action.onClick(); }}>{action.label}</button>)}
    </div>, trigger.current?.closest('dialog') || document.body)}
  </>;
}

export function Skeleton({ widths }: { widths: string[] }) {
  return <div className="skeleton">{widths.map((width, index) => <i key={index} style={{ width, animationDelay: `${index * 0.15}s` }} />)}</div>;
}

export function Empty({ title, copy, action }: { title: string; copy?: string; action?: ReactNode }) {
  return <div className="empty"><b>{title}</b>{copy && <p>{copy}</p>}{action}</div>;
}

export function RequirementHint({ missing, label = "필수 입력이 남았습니다", className = "" }: { missing: string[]; label?: string; className?: string }) {
  if (!missing.length) return null;
  return <p className={`requirement-hint ${className}`.trim()} role="status"><AlertTriangle size={14} /><span><b>{label}</b> · {missing.join(" · ")}</span></p>;
}

export function focusInvalidField(root: ParentNode) {
  requestAnimationFrame(() => {
    const field = root.querySelector<HTMLElement>('.field-missing,[aria-invalid="true"],input:invalid,select:invalid,textarea:invalid');
    if (!field) return;
    const control = field.matches('input,select,textarea,button,[contenteditable="true"]') ? field
      : field.querySelector<HTMLElement>('[contenteditable="true"]') ?? field.querySelector<HTMLElement>('input,select,textarea,button');
    control?.focus({ preventScroll: true });
    field.scrollIntoView({ behavior: "smooth", block: "center" });
  });
}

/** 표 머리와 본문이 같은 그리드를 쓰도록 열 정의를 컨테이너에 둔다. */
export function DataTable({ cols, dense, children }: { cols?: string; dense?: boolean; children: ReactNode }) {
  return <div className={`data-table ${dense ? "dense" : ""}`} style={cols ? ({ "--cols": cols } as CSSProperties) : undefined}>{children}</div>;
}

export function TableFoot({ count, total, hint }: { count: number; total: number; hint: string }) {
  return <div className="table-foot"><span className="mono">{count} / {total}</span><span>{hint}</span></div>;
}

export function KanbanPatternBar({ users }: { users: User[] }) {
  if (!users.length) return null;
  return <span className="kanban-pattern-bar" aria-hidden="true">{users.slice(0, 3).map((user) =>
    <i key={user.id} className={kanbanStyleClass(user.kanbanPattern, user.kanbanColor)} />
  )}</span>;
}

export function Pagination({ page, totalPages, onPage }: { page: number; totalPages: number; onPage: (page: number) => void }) {
  const { t } = useTranslation();
  if (totalPages <= 1) return null;
  const first = Math.max(0, Math.min(page - 2, totalPages - 5));
  const pages = Array.from({ length: Math.min(5, totalPages) }, (_, index) => first + index);
  return <nav className="pagination" aria-label={t("common.pagination")}>
    <button type="button" aria-label={t("common.previousPage")} disabled={page === 0} onClick={() => onPage(page - 1)}><ChevronLeft size={14} /></button>
    {pages.map((index) => <button type="button" key={index} aria-label={t("common.page", { page: index + 1 })} aria-current={index === page ? "page" : undefined} onClick={() => onPage(index)}>{index + 1}</button>)}
    <button type="button" aria-label={t("common.nextPage")} disabled={page >= totalPages - 1} onClick={() => onPage(page + 1)}><ChevronRight size={14} /></button>
  </nav>;
}

export function Avatar({ name, size, userId, avatarVersion }: { name: string; size?: "sm" | "lg"; userId?: string; avatarVersion?: string | null }) {
  const { t } = useTranslation();
  const [source, setSource] = useState<string>();
  useEffect(() => {
    if (!userId || !avatarVersion) { setSource(undefined); return; }
    const publicUrl = api.publicAvatarUrl(userId, avatarVersion);
    if (publicUrl) { setSource(publicUrl); return; }
    let active = true;
    let objectUrl = "";
    void api.userAvatar(userId, avatarVersion).then((blob) => {
      if (!active) return;
      objectUrl = URL.createObjectURL(blob);
      setSource(objectUrl);
    }).catch(() => { if (active) setSource(undefined); });
    return () => { active = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [avatarVersion, userId]);
  return <span className={`avatar ${size || ""}`}>{source
    ? <img src={source} alt={t("common.profileImage", { name })} onError={() => setSource(undefined)} />
    : initials(name).slice(0, 1)}</span>;
}

export function Status({ status }: { status: Task["status"] }) {
  const { t } = useTranslation();
  return <span className={`status status-${status.toLowerCase()}`}><i />{t(`status.${status}`, { defaultValue: statusText[status] })}</span>;
}

export function SpineField({ label, help, children }: { label: string; help?: ReactNode; children: ReactNode }) {
  return <div className="spine-field"><div className="spine-label">{label}{help}</div><div className="spine-value">{children}</div></div>;
}

export function SealMark({ sealed, at, by, sealedLabel = "확정", unsealedLabel = "미확정" }: {
  sealed: boolean; at?: string; by?: string; sealedLabel?: string; unsealedLabel?: string;
}) {
  return <div className={sealed ? "seal sealed" : "seal unsealed"}>
    <span className="seal-face">{sealed ? sealedLabel : unsealedLabel}</span>
    {sealed && at && <span className="seal-meta mono">{at}{by ? ` · ${by}` : ""}</span>}
  </div>;
}

export function ImportanceBar({ score, label }: { score: number; label?: string | null }) {
  return <div className="importance"><div className="importance-bar"><i style={{ width: `${Math.max(0, Math.min(100, score))}%` }} /></div><span className="mono">{score}%</span>{label && <small>{label}</small>}</div>;
}

export function LevelSegments({ difficulty }: { difficulty: Task["difficulty"] }) {
  const { t } = useTranslation();
  const rank = difficultyRank[difficulty];
  return <span className="level"><span className="level-bars">{[1, 2, 3].map((step) => <i key={step} className={step <= rank ? `on step-${step}` : ""} />)}</span>{t(`difficulty.${difficulty}`, { defaultValue: difficultyText[difficulty] })}</span>;
}

export function ScoreScale({ value, max = 5, disabled, onPick }: { value: number | null; max?: number; disabled?: boolean; onPick: (score: number) => void }) {
  return <div className="score-scale" role="group">{Array.from({ length: max }, (_, index) => index + 1).map((score) => <button
    type="button" key={score} className={value === score ? "picked" : ""} disabled={disabled} aria-pressed={value === score} onClick={() => onPick(score)}>{score}</button>)}</div>;
}

export function Toggle({ on, label, disabled, onChange }: { on: boolean; label: string; disabled?: boolean; onChange: (next: boolean) => void }) {
  return <button type="button" className={`toggle ${on ? "on" : ""}`} role="switch" aria-checked={on} aria-label={label} disabled={disabled} onClick={() => onChange(!on)}><span /></button>;
}

export function Meter({ ratio, tone }: { ratio: number; tone?: string }) {
  return <span className={`meter ${tone || ""}`}><i style={{ width: `${Math.max(0, Math.min(100, ratio))}%` }} /></span>;
}

export function ModalFrame({ title, eyebrow, wide, compact, headerActions, onClose, children }: { title: string; eyebrow: string; wide?: boolean; compact?: boolean; headerActions?: ReactNode; onClose: () => void; children: ReactNode }) {
  const { t } = useTranslation();
  const dialog = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useLayoutEffect(() => {
    const element = dialog.current!;
    element.showModal();
    return () => element.close();
  }, []);
  return createPortal(<dialog ref={dialog} className="modal-layer" aria-labelledby={titleId} aria-modal="true"
    onInvalidCapture={event => {
      (event.target as HTMLElement).closest("form")?.classList.add("was-validated");
      focusInvalidField(event.currentTarget);
    }}
    onCancel={event => { event.preventDefault(); event.stopPropagation(); onClose(); }}
    onKeyDown={event => { if (event.key === "Escape" && !event.defaultPrevented) { event.preventDefault(); event.stopPropagation(); onClose(); } }}>
    <button type="button" className="modal-backdrop" tabIndex={-1} onClick={onClose} aria-label={t("common.close")} /><section className={`modal ${wide ? "wide" : ""} ${compact ? "compact" : ""}`}>
    <header><div><span className="mono-label">{eyebrow}</span><h2 id={titleId}>{title}</h2></div><span className="modal-head-actions">{headerActions}<button type="button" autoFocus onClick={onClose} aria-label={t("common.close")}><X size={18} /></button></span></header>{children}
  </section></dialog>, document.body);
}

export function ConfirmDialog({ title, eyebrow, confirmLabel, busy, onClose, onConfirm, children }: { title: string; eyebrow: string; confirmLabel: string; busy: boolean; onClose: () => void; onConfirm: () => Promise<void>; children: ReactNode }) {
  const { t } = useTranslation();
  return <ModalFrame title={title} eyebrow={eyebrow} onClose={onClose}><div className="confirm-dialog">
    <span className="warning-mark"><AlertTriangle size={22} /></span><div>{children}</div>
    <footer><button onClick={onClose}>{t("common.back")}</button><button className="danger" disabled={busy} onClick={() => void onConfirm()}>{busy ? t("common.processing") : confirmLabel}</button></footer>
  </div></ModalFrame>;
}

export function AsideCard({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return <div className="aside-card"><div className="aside-title">{title}</div>{hint && <p className="aside-hint">{hint}</p>}{children}</div>;
}

export function Info({ label, value }: { label: string; value: string }) {
  return <div className="info"><span>{label}</span><b>{value}</b></div>;
}

export function KeyValue({ rows }: { rows: [string, ReactNode][] }) {
  return <dl className="key-value">{rows.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}</dl>;
}

export function depthStyle(depth: number) {
  return { "--tree-depth": depth } as CSSProperties;
}
